import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthProvider";
import { motion, AnimatePresence } from "framer-motion";
import { ExclamationTriangleIcon, InformationCircleIcon } from "@heroicons/react/24/outline";

// Temps d'inactivité autorisé avant avertissement (28 minutes)
const WARNING_TIMEOUT = 28 * 60 * 1000;
// Temps supplémentaire accordé pour réagir au modal (2 minutes) -> 30 minutes au total
const LOGOUT_COUNTDOWN = 120; // secondes

export default function SessionManager() {
  const { user, logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [showDisconnectedModal, setShowDisconnectedModal] = useState(false);
  const [countdown, setCountdown] = useState(LOGOUT_COUNTDOWN);

  // Mise à jour sécurisée du timer dans le localStorage
  const updateActivity = useCallback(() => {
    // Si on est en train d'afficher l'avertissement, on réinitialise
    if (showWarning) {
      setShowWarning(false);
      setCountdown(LOGOUT_COUNTDOWN);
    }
    localStorage.setItem("lastActivity", Date.now().toString());
  }, [showWarning]);

  useEffect(() => {
    if (!user) return; // Le tracker ne tourne que si l'utilisateur est connecté

    // Initialisation
    localStorage.setItem("lastActivity", Date.now().toString());

    // Liste des événements considérés comme une "activité"
    const events = ["mousemove", "keydown", "mousedown", "touchstart", "scroll"];
    
    // Fonction de debouncing pour ne pas spammer le localStorage
    let throttleTimer;
    const handleActivity = () => {
      if (throttleTimer) return;
      throttleTimer = setTimeout(() => {
        updateActivity();
        throttleTimer = null;
      }, 5000); // 1 mise à jour toutes les 5 secondes maximum
    };

    events.forEach(event => window.addEventListener(event, handleActivity));

    // Intervalle de vérification d'inactivité
    const interval = setInterval(() => {
      const lastActivityStr = localStorage.getItem("lastActivity");
      if (!lastActivityStr) return;
      
      const lastActivity = parseInt(lastActivityStr, 10);
      const timeInactive = Date.now() - lastActivity;

      if (timeInactive >= WARNING_TIMEOUT && !showWarning) {
        setShowWarning(true);
      }
    }, 10000); // On vérifie toutes les 10 secondes

    return () => {
      events.forEach(event => window.removeEventListener(event, handleActivity));
      if (throttleTimer) clearTimeout(throttleTimer);
      clearInterval(interval);
    };
  }, [user, updateActivity, showWarning]);

  // Logique du compte à rebours de déconnexion
  useEffect(() => {
    if (showWarning) {
      if (countdown <= 0) {
        logout(); // Déconnexion silencieuse
        setShowWarning(false);
        setShowDisconnectedModal(true);
        return;
      }

      const timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [showWarning, countdown, logout]);

  return (
    <AnimatePresence>
      {showWarning && !showDisconnectedModal && (
        <motion.div
          key="warning-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            className="workspace-card-elevated max-w-md w-full mx-4 p-8 text-center"
          >
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-warning-100 mb-6">
              <ExclamationTriangleIcon className="h-10 w-10 text-warning-600" aria-hidden="true" />
            </div>
            <h2 className="text-2xl font-bold text-primary mb-4">Inactivité détectée</h2>
            <p className="text-subtle mb-6 text-lg">
              Votre session va expirer dans <span className="font-bold text-danger-500">{countdown}s</span>.<br/>
              Bougez la souris ou cliquez sur le bouton pour rester connecté.
            </p>
            <button
              onClick={updateActivity}
              className="w-full flex justify-center rounded-md bg-primary px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary transition-colors"
            >
              Je suis toujours là
            </button>
          </motion.div>
        </motion.div>
      )}

      {showDisconnectedModal && (
        <motion.div
          key="disconnected-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            className="workspace-card-elevated max-w-md w-full mx-4 p-8 text-center"
          >
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary-light mb-6">
              <InformationCircleIcon className="h-10 w-10 text-primary" aria-hidden="true" />
            </div>
            <h2 className="text-2xl font-bold text-primary mb-4">Session expirée</h2>
            <p className="text-subtle mb-6 text-lg">
              Vous avez été déconnecté pour inactivité, merci de vous reconnecter.
            </p>
            <button
              onClick={() => setShowDisconnectedModal(false)}
              className="w-full flex justify-center rounded-md bg-primary px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary transition-colors"
            >
              Reconnexion
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
